/**
 * Date/Time utility functions with timezone support
 */

// Get user's stored timezone from localStorage or default to Eastern Time (EST/EDT)
export const getUserTimezone = () => {
  const storedTz = localStorage.getItem('userTimezone');
  if (storedTz) return storedTz;

  // Default to Eastern Time — matches the global Date.prototype patch in
  // utils/timezonePatch.js so every surface renders in the same zone.
  return 'America/New_York';
};

// Set user's timezone in localStorage
export const setUserTimezone = (tz) => {
  if (tz) {
    localStorage.setItem('userTimezone', tz);
  }
};

// Get user's timezone abbreviation
export const getTimezoneAbbr = (tz = null) => {
  const timezone = tz || getUserTimezone();
  const date = new Date();
  
  try {
    // Get timezone abbreviation (e.g., EST, PST, UTC)
    const abbr = date.toLocaleTimeString('en-US', { 
      timeZone: timezone,
      timeZoneName: 'short' 
    }).split(' ').pop();
    
    return abbr;
  } catch (e) {
    // Fallback to browser timezone
    return date.toLocaleTimeString('en-US', { 
      timeZoneName: 'short' 
    }).split(' ').pop();
  }
};

// Get full timezone name
export const getTimezoneName = (tz = null) => {
  return tz || getUserTimezone();
};

// Format date with timezone
export const formatDateWithTZ = (dateString, options = {}, tz = null) => {
  if (!dateString) return '';
  
  const timezone = tz || getUserTimezone();
  const date = new Date(dateString);
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
    timeZoneName: 'short'
  };
  
  try {
    return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
  } catch (e) {
    return date.toLocaleDateString('en-US', { ...defaultOptions, ...options, timeZone: undefined });
  }
};

// Format time with timezone
export const formatTimeWithTZ = (dateString, options = {}, tz = null) => {
  if (!dateString) return '';
  
  const timezone = tz || getUserTimezone();
  const date = new Date(dateString);
  const defaultOptions = {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
    timeZoneName: 'short'
  };
  
  try {
    return date.toLocaleTimeString('en-US', { ...defaultOptions, ...options });
  } catch (e) {
    return date.toLocaleTimeString('en-US', { ...defaultOptions, ...options, timeZone: undefined });
  }
};

// Format full datetime with timezone
export const formatDateTimeWithTZ = (dateString, options = {}, tz = null) => {
  if (!dateString) return '';
  
  const timezone = tz || getUserTimezone();
  const date = new Date(dateString);
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
    timeZoneName: 'short'
  };
  
  try {
    return date.toLocaleString('en-US', { ...defaultOptions, ...options });
  } catch (e) {
    return date.toLocaleString('en-US', { ...defaultOptions, ...options, timeZone: undefined });
  }
};

// Format relative time (e.g., "2 hours ago") with timezone hint
export const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return formatDateWithTZ(dateString);
};

// Format for scheduled jobs - compact with timezone
export const formatScheduledDateTime = (dateString, tz = null) => {
  if (!dateString) return '';
  
  const timezone = tz || getUserTimezone();
  const date = new Date(dateString);
  const tzAbbr = getTimezoneAbbr(timezone);
  
  try {
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone
    }) + ` ${tzAbbr}`;
  } catch (e) {
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }) + ` ${tzAbbr}`;
  }
};

// Format for message timestamps - short time with timezone on hover
export const formatMessageTime = (dateString, tz = null) => {
  if (!dateString) return '';
  
  const timezone = tz || getUserTimezone();
  const date = new Date(dateString);
  
  try {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone
    });
  } catch (e) {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

// Format date-only (date + TZ-aware, no time) — for scheduled-job cards that
// only need the day. Centralizes the ad-hoc `new Date(x).toLocaleDateString()`
// copies that previously lived in BrowseJobs / UpcomingJobCard / JobsApplied.
export const formatScheduledDateShort = (dateString, tz = null) => {
  if (!dateString) return '';
  const timezone = tz || getUserTimezone();
  const date = new Date(dateString);
  try {
    return date.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', timeZone: timezone
    });
  } catch (e) {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }
};

// Convert a stored scheduled_date (ISO, possibly offset-aware) into the
// {date:'YYYY-MM-DD', time:'HH:mm'} pair shown in edit-form inputs — computed
// in the DISPLAY timezone so the edit form matches what the user sees on the
// page. Replaces the brittle `scheduled_date.split('T')` extraction, which used
// the raw stored wall-clock and could drift from the displayed local time.
export const getScheduleInputValues = (dateString, tz = null) => {
  if (!dateString) return { date: '', time: '' };
  const timezone = tz || getUserTimezone();
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return { date: '', time: '' };
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timezone
    }).formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const hour = parts.hour === '24' ? '00' : parts.hour;
    return {
      date: `${parts.year}-${parts.month}-${parts.day}`,
      time: `${hour}:${parts.minute}`
    };
  } catch (e) {
    return { date: '', time: '' };
  }
};

// Get timezone display string for UI labels
export const getTimezoneDisplay = (tz = null) => {
  const timezone = tz || getUserTimezone();
  const tzAbbr = getTimezoneAbbr(timezone);
  return `${tzAbbr} (${timezone})`;
};

// Format date-only (no time, no TZ indicator) — for DOB, member-since, etc.
export const formatDate = (dateString, options = {}, tz = null) => {
  if (!dateString) return '';
  const timezone = tz || getUserTimezone();
  const date = new Date(dateString);
  const defaultOptions = { year: 'numeric', month: 'short', day: 'numeric', timeZone: timezone };
  try {
    return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
  } catch (e) {
    return date.toLocaleDateString('en-US', { ...defaultOptions, ...options, timeZone: undefined });
  }
};

// Format datetime (date + time, no TZ label) — for last active, transaction timestamps
export const formatDateTime = (dateString, options = {}, tz = null) => {
  if (!dateString) return '';
  const timezone = tz || getUserTimezone();
  const date = new Date(dateString);
  const defaultOptions = {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: timezone
  };
  try {
    return date.toLocaleString('en-US', { ...defaultOptions, ...options });
  } catch (e) {
    return date.toLocaleString('en-US', { ...defaultOptions, ...options, timeZone: undefined });
  }
};

// ===== Eastern-time calendar helpers =====
// Admin date filters must use the ET calendar day, not the pod's UTC day —
// otherwise "today" flips a day early every evening after 8 PM ET.
export const todayET = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

export const daysAgoET = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
};

export const yearStartET = () =>
  `${new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }).slice(0, 4)}-01-01`;
